import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Server,
  Users,
  Route,
  Gauge,
  FileText,
  Activity,
  Settings,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Shield
} from 'lucide-react';
import api from '../../services/api';

const SMPPManagement = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchMetrics();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchMetrics, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/super/smpp/metrics');
      setMetrics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch SMPP metrics:', error);
      setLoading(false);
    }
  };

  const modules = [
    {
      title: 'Outbound Accounts',
      description: 'Manage SMPP server connections and operators',
      icon: Server,
      path: '/super/smpp/connections',
      color: 'blue',
      stats: metrics ? `${metrics.active_connections} Active` : '-'
    },
    {
      title: 'Inbound Accounts',
      description: 'Manage client SMPP user accounts',
      icon: Users,
      path: '/super/smpp/inbound-accounts',
      color: 'green',
      stats: 'User Management'
    },
    {
      title: 'Routing Management',
      description: 'Configure operator routing rules',
      icon: Route,
      path: '/super/smpp/routes',
      color: 'purple',
      stats: 'Route Config'
    },
    {
      title: 'Routing Engine',
      description: 'Control routing mode and failover',
      icon: Settings,
      path: '/super/smpp/routing-engine',
      color: 'indigo',
      stats: 'Engine Control'
    },
    {
      title: 'TPS & Throttling',
      description: 'Monitor and control TPS limits',
      icon: Gauge,
      path: '/super/smpp/tps-control',
      color: 'yellow',
      stats: metrics ? `${metrics.current_tps_inbound + metrics.current_tps_outbound} TPS` : '-'
    },
    {
      title: 'DLR Monitoring',
      description: 'Track delivery receipt pipeline',
      icon: Activity,
      path: '/super/smpp/dlr-monitoring',
      color: 'pink',
      stats: 'DLR Pipeline'
    },
    {
      title: 'SMPP Logs',
      description: 'View operational logs and audit trail',
      icon: FileText,
      path: '/super/smpp/logs',
      color: 'gray',
      stats: 'Audit Trail'
    },
    {
      title: 'Metrics Dashboard',
      description: 'Real-time SMPP performance metrics',
      icon: TrendingUp,
      path: '/super/smpp/metrics',
      color: 'cyan',
      stats: 'Live Metrics'
    },
    {
      title: 'Audit Dashboard',
      description: 'Submit vs Response reconciliation audit',
      icon: Shield,
      path: '/super/smpp/audit',
      color: 'red',
      stats: 'Reconciliation'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    pink: 'bg-pink-50 text-pink-600 border-pink-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-200'
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SMPP Management</h1>
          <p className="text-gray-600 mt-2">
            Centralized control and monitoring for all SMPP operations
          </p>
        </div>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="sr-only peer"
          />
          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-700">Auto-refresh</span>
        </label>
      </div>

      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inbound TPS</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.current_tps_inbound}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Outbound TPS</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.current_tps_outbound}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Connections</p>
                <p className="text-2xl font-bold text-gray-900">{metrics.active_connections}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Error Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metrics.error_rate?.error_rate_percent?.toFixed(2) || 0}%
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <Link
              key={module.path}
              to={module.path}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden border border-gray-200 hover:border-gray-300"
            >
              <div className={`p-4 border-b-2 ${colorClasses[module.color]}`}>
                <Icon className="w-8 h-8" />
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {module.title}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {module.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">
                    {module.stats}
                  </span>
                  <span className="text-xs text-blue-600 font-medium">
                    Manage →
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-yellow-900 mb-1">
              Master Admin Access Only
            </h4>
            <p className="text-sm text-yellow-800">
              All SMPP management functions are restricted to Master Admin role. 
              Changes made here affect the entire platform. Exercise caution when 
              modifying connection settings, TPS limits, or routing rules.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMPPManagement;
